import React, { useState } from 'react';
import { api } from '../../services/api';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Shield, ShieldOff, Pause, Play, X, Check, Send, FileText, Users, XCircle, RefreshCw, UserPlus } from 'lucide-react';

function ActionButton({ icon: Icon, label, onClick, variant = 'outline', className = '', disabled = false, testId, tooltip }) {
  return (
    <div className="relative group">
      <Button variant={variant} size="sm" className={`gap-1.5 text-xs h-8 ${className}`} onClick={onClick} disabled={disabled} data-testid={testId}>
        {Icon && <Icon className="h-3 w-3" />}{label}
      </Button>
      {disabled && tooltip && <div className="absolute bottom-full mb-1 left-0 hidden group-hover:block bg-popover text-popover-foreground text-[10px] px-2 py-1 rounded shadow-md border whitespace-nowrap z-50">{tooltip}</div>}
    </div>
  );
}

function ReasonDialog({ open, onClose, title, onSubmit, requireReason = true, fields = [] }) {
  const [reason, setReason] = useState('');
  const [extra, setExtra] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (requireReason && !reason.trim()) { toast.error('Reason is required.'); return; }
    setSubmitting(true);
    try {
      await onSubmit({ reason, ...extra });
      setReason(''); setExtra({});
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Action failed.');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="text-base">{title}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <Textarea placeholder="Reason..." value={reason} onChange={e => setReason(e.target.value)} className="text-sm min-h-[60px]" data-testid="action-reason" />
          {fields.map(f => (
            <div key={f.key}>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">{f.label}</label>
              {f.type === 'select' ? (
                <Select value={extra[f.key] || ''} onValueChange={v => setExtra(p => ({ ...p, [f.key]: v }))}>
                  <SelectTrigger className="text-sm"><SelectValue placeholder={f.placeholder} /></SelectTrigger>
                  <SelectContent>{f.options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Input type={f.type || 'text'} placeholder={f.placeholder} value={extra[f.key] || ''} onChange={e => setExtra(p => ({ ...p, [f.key]: e.target.value }))} className="text-sm" data-testid={`action-field-${f.key}`} />
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={handleSubmit} disabled={submitting} data-testid="action-confirm">{submitting ? 'Processing...' : 'Confirm'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function RevenueActionPanel({ caseData, onRefresh }) {
  const [dialog, setDialog] = useState(null);
  if (!caseData) return null;

  const c = caseData;
  const caseId = c.id;
  const state = c.state || '';
  const effQueue = c.effective_queue || '';
  const stmtStatus = c.statement_readiness_status || '';
  const collStatus = c.collections_readiness_status || '';
  const woStatus = c.writeoff_readiness_status || '';
  const isResolved = state === 'resolved';
  const hasHold = c.manual_hold_active;
  const hasSuppress = c.manual_suppress_active;

  const doAction = async (endpoint, body = {}) => {
    await api.post(endpoint, body);
    toast.success('Action completed.');
    onRefresh?.();
  };

  const openDialog = (config) => setDialog(config);
  const closeDialog = () => setDialog(null);

  return (
    <div className="space-y-4" data-testid="revenue-action-panel">
      {/* Responsibility Controls */}
      <div>
        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Responsibility Controls</div>
        <div className="flex flex-wrap gap-2">
          {!hasHold && !isResolved && (
            <ActionButton icon={Shield} label="Hold" testId="action-hold" onClick={() => openDialog({
              title: 'Apply Manual Hold', onSubmit: d => doAction(`/revenue/responsibility/${caseId}/manual-hold`, d),
            })} />
          )}
          {hasHold && <ActionButton icon={ShieldOff} label="Release Hold" testId="action-release" onClick={() => doAction(`/revenue/responsibility/${caseId}/manual-release`)} />}
          {!hasSuppress && !isResolved && (
            <ActionButton icon={X} label="Suppress" testId="action-suppress" onClick={() => openDialog({
              title: 'Suppress Case', onSubmit: d => doAction(`/revenue/responsibility/${caseId}/manual-suppress`, d),
            })} />
          )}
          {hasSuppress && <ActionButton icon={RefreshCw} label="Unsuppress" testId="action-unsuppress" onClick={() => doAction(`/revenue/responsibility/${caseId}/manual-unsuppress`)} />}
        </div>
      </div>

      {/* Statement Controls */}
      {effQueue === 'patient_responsibility_ready' && (
        <div>
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Statement Workflow</div>
          <div className="flex flex-wrap gap-2">
            {(stmtStatus === 'ready_for_statement_review' || stmtStatus === 'not_applicable' || !stmtStatus) && (
              <ActionButton icon={FileText} label="Evaluate Readiness" testId="action-stmt-eval" onClick={async () => { await api.get(`/revenue/statements/${caseId}/readiness`); toast.success('Readiness evaluated.'); onRefresh?.(); }} />
            )}
            {stmtStatus === 'ready_for_statement_review' && (
              <ActionButton icon={Check} label="Approve Statement" testId="action-stmt-approve" variant="default" onClick={() => openDialog({
                title: 'Approve for Statement', onSubmit: d => doAction(`/revenue/statements/${caseId}/approve`, d), requireReason: false,
              })} />
            )}
            {stmtStatus === 'approved_for_statement_generation' && (
              <>
                <ActionButton icon={FileText} label="Generate Artifact" testId="action-stmt-generate" onClick={() => openDialog({
                  title: 'Generate Statement Artifact', onSubmit: d => doAction(`/revenue/statements/${caseId}/generate`, d), requireReason: false,
                })} />
                <ActionButton icon={RefreshCw} label="Regenerate" testId="action-stmt-regenerate" onClick={() => openDialog({
                  title: 'Regenerate Statement', onSubmit: d => doAction(`/revenue/statements/${caseId}/regenerate`, d), requireReason: false,
                })} />
                <ActionButton icon={Send} label="Send Statement" testId="action-stmt-send" onClick={() => openDialog({
                  title: 'Send Statement', onSubmit: async d => {
                    const arts = (await api.get(`/revenue/statements/${caseId}/artifacts`)).data.artifacts;
                    const current = arts.find(a => a.is_current);
                    if (!current) throw new Error('No current artifact.');
                    await api.post(`/revenue/statements/artifacts/${current.id}/send`, { channel: d.channel || 'email', destination: d.destination || '', reason: d.reason });
                  },
                  fields: [
                    { key: 'channel', label: 'Channel', type: 'select', placeholder: 'Select channel', options: [{ value: 'email', label: 'Email' }, { value: 'sms', label: 'SMS' }, { value: 'print_mail', label: 'Print/Mail' }] },
                    { key: 'destination', label: 'Destination', placeholder: 'patient@email.com' },
                  ],
                })} />
              </>
            )}
            {!['not_applicable', 'manually_suppressed', 'manually_deferred'].includes(stmtStatus) && stmtStatus && (
              <>
                <ActionButton icon={Pause} label="Defer Stmt" testId="action-stmt-defer" onClick={() => openDialog({
                  title: 'Defer Statement', onSubmit: d => doAction(`/revenue/statements/${caseId}/defer`, { ...d, defer_until: d.defer_until || undefined }),
                  fields: [{ key: 'defer_until', label: 'Defer Until (optional)', type: 'datetime-local' }],
                })} />
                <ActionButton icon={X} label="Suppress Stmt" testId="action-stmt-suppress" onClick={() => openDialog({
                  title: 'Suppress Statement', onSubmit: d => doAction(`/revenue/statements/${caseId}/suppress`, d),
                })} />
              </>
            )}
            {(stmtStatus === 'manually_suppressed' || stmtStatus === 'manually_deferred') && (
              <ActionButton icon={RefreshCw} label="Unsuppress Stmt" testId="action-stmt-unsuppress" onClick={() => doAction(`/revenue/statements/${caseId}/unsuppress`)} />
            )}
          </div>
        </div>
      )}

      {/* Collections Controls */}
      {(collStatus || c.collections_readiness_status) && (
        <div>
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Collections Workflow</div>
          <div className="flex flex-wrap gap-2">
            {(!collStatus || collStatus === 'not_applicable' || collStatus === 'not_ready_recent_statement') && (
              <ActionButton icon={Users} label="Evaluate Collections" testId="action-coll-eval" onClick={async () => { await api.get(`/revenue/collections/${caseId}/status`); toast.success('Collections readiness evaluated.'); onRefresh?.(); }} />
            )}
            {collStatus === 'ready_for_collections_review' && (
              <ActionButton icon={Check} label="Approve Collections" testId="action-coll-approve" variant="default" onClick={() => openDialog({
                title: 'Approve for Collections', onSubmit: d => doAction(`/revenue/collections/${caseId}/approve`, d), requireReason: false,
              })} />
            )}
            {collStatus === 'approved_for_collections' && (
              <>
                <ActionButton icon={Users} label="Start Internal" testId="action-coll-start" onClick={() => openDialog({
                  title: 'Start Internal Collections', onSubmit: d => doAction(`/revenue/collections/${caseId}/start-internal`, d),
                  fields: [{ key: 'notes', label: 'Notes', placeholder: 'Additional notes...' }],
                })} />
                <ActionButton icon={Send} label="Handoff Agency" testId="action-coll-handoff" onClick={() => openDialog({
                  title: 'Handoff to Agency', onSubmit: d => doAction(`/revenue/collections/${caseId}/handoff-agency`, { ...d, agency_vendor: d.agency_vendor || 'PLACEHOLDER' }),
                  fields: [{ key: 'agency_vendor', label: 'Agency Vendor', placeholder: 'Agency name' }],
                })} />
              </>
            )}
            {!['not_applicable', 'manually_suppressed', 'manually_deferred'].includes(collStatus) && collStatus && (
              <>
                <ActionButton icon={Pause} label="Defer Coll" testId="action-coll-defer" onClick={() => openDialog({
                  title: 'Defer Collections', onSubmit: d => doAction(`/revenue/collections/${caseId}/defer`, d),
                })} />
                <ActionButton icon={X} label="Suppress Coll" testId="action-coll-suppress" onClick={() => openDialog({
                  title: 'Suppress Collections', onSubmit: d => doAction(`/revenue/collections/${caseId}/suppress`, d),
                })} />
              </>
            )}
            {(collStatus === 'manually_suppressed' || collStatus === 'manually_deferred') && (
              <ActionButton icon={RefreshCw} label="Unsuppress Coll" testId="action-coll-unsuppress" onClick={() => doAction(`/revenue/collections/${caseId}/unsuppress`)} />
            )}
          </div>
        </div>
      )}

      {/* Collections Execution Controls (when action exists) */}
      {c._collAction && (
        <div>
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Collections Execution</div>
          <div className="flex flex-wrap gap-2">
            {c._collAction.execution_status === 'active' && <ActionButton icon={Pause} label="Pause" testId="action-coll-pause" onClick={() => openDialog({ title: 'Pause Collections', onSubmit: d => doAction(`/revenue/collections/${caseId}/pause`, d) })} />}
            {c._collAction.execution_status === 'paused' && <ActionButton icon={Play} label="Resume" testId="action-coll-resume" onClick={() => openDialog({ title: 'Resume Collections', onSubmit: d => doAction(`/revenue/collections/${caseId}/resume`, d) })} />}
            {['active', 'paused'].includes(c._collAction?.execution_status) && <ActionButton icon={X} label="Close" testId="action-coll-close" onClick={() => openDialog({
              title: 'Close Collections Action', onSubmit: d => doAction(`/revenue/collections/${caseId}/close`, { closure_reason: d.closure_reason || d.reason, notes: d.notes }),
              fields: [
                { key: 'closure_reason', label: 'Closure Reason', type: 'select', placeholder: 'Select reason', options: [{ value: 'PAID_IN_FULL', label: 'Paid in Full' }, { value: 'SETTLED', label: 'Settled' }, { value: 'RETURNED_TO_STATEMENT_WORKFLOW', label: 'Return to Statements' }, { value: 'SUPPRESSED', label: 'Suppressed' }, { value: 'OTHER', label: 'Other' }] },
                { key: 'notes', label: 'Notes', placeholder: 'Additional notes...' },
              ],
            })} />}
          </div>
        </div>
      )}

      {/* Write-Off Controls */}
      {(woStatus || c.writeoff_readiness_status) && (
        <div>
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Write-Off Workflow</div>
          <div className="flex flex-wrap gap-2">
            {(!woStatus || woStatus === 'not_applicable') && (
              <ActionButton icon={XCircle} label="Evaluate Write-Off" testId="action-wo-eval" onClick={async () => { await api.get(`/revenue/writeoff/${caseId}/status`); toast.success('Write-off readiness evaluated.'); onRefresh?.(); }} />
            )}
            {woStatus === 'ready_for_writeoff_review' && (
              <ActionButton icon={Check} label="Approve Write-Off" testId="action-wo-approve" variant="default" onClick={() => openDialog({
                title: 'Approve for Write-Off', onSubmit: d => doAction(`/revenue/writeoff/${caseId}/approve-review`, d), requireReason: false,
              })} />
            )}
            {woStatus === 'approved_for_writeoff_execution' && (
              <ActionButton icon={XCircle} label="Execute Write-Off" testId="action-wo-execute" onClick={() => openDialog({
                title: 'Execute Write-Off', onSubmit: d => doAction(`/revenue/writeoff/${caseId}/execute`, { ...d, writeoff_reason_code: d.writeoff_reason_code || 'BAD_DEBT' }),
                fields: [
                  { key: 'writeoff_reason_code', label: 'Reason Code', type: 'select', placeholder: 'Select code', options: [{ value: 'BAD_DEBT', label: 'Bad Debt' }, { value: 'CHARITY', label: 'Charity' }, { value: 'ADMINISTRATIVE', label: 'Administrative' }, { value: 'OTHER', label: 'Other' }] },
                  { key: 'notes', label: 'Notes', placeholder: 'Additional notes...' },
                ],
              })} />
            )}
            {!['not_applicable', 'manually_suppressed', 'manually_deferred'].includes(woStatus) && woStatus && (
              <>
                <ActionButton icon={Pause} label="Defer WO" testId="action-wo-defer" onClick={() => openDialog({ title: 'Defer Write-Off', onSubmit: d => doAction(`/revenue/writeoff/${caseId}/defer`, d) })} />
                <ActionButton icon={X} label="Suppress WO" testId="action-wo-suppress" onClick={() => openDialog({ title: 'Suppress Write-Off', onSubmit: d => doAction(`/revenue/writeoff/${caseId}/suppress`, d) })} />
              </>
            )}
            {(woStatus === 'manually_suppressed' || woStatus === 'manually_deferred') && (
              <ActionButton icon={RefreshCw} label="Unsuppress WO" testId="action-wo-unsuppress" onClick={() => doAction(`/revenue/writeoff/${caseId}/unsuppress`)} />
            )}
          </div>
        </div>
      )}

      {/* Write-Off Execution Controls */}
      {c._woAction && ['active', 'paused'].includes(c._woAction?.execution_status) && (
        <div>
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Write-Off Execution</div>
          <div className="flex flex-wrap gap-2">
            {c._woAction.execution_status === 'active' && <ActionButton icon={Pause} label="Pause" testId="action-wo-pause" onClick={() => openDialog({ title: 'Pause Write-Off', onSubmit: d => doAction(`/revenue/writeoff/${caseId}/pause`, d) })} />}
            {c._woAction.execution_status === 'paused' && <ActionButton icon={Play} label="Resume" testId="action-wo-resume" onClick={() => openDialog({ title: 'Resume Write-Off', onSubmit: d => doAction(`/revenue/writeoff/${caseId}/resume`, d) })} />}
            <ActionButton icon={X} label="Close" testId="action-wo-close" onClick={() => openDialog({
              title: 'Close Write-Off', onSubmit: d => doAction(`/revenue/writeoff/${caseId}/close`, { closure_reason: d.closure_reason || d.reason }),
              fields: [{ key: 'closure_reason', label: 'Closure Reason', type: 'select', placeholder: 'Select', options: [{ value: 'COMPLETED', label: 'Completed' }, { value: 'REVERSED_OR_SUPERSEDED', label: 'Reversed' }, { value: 'PAID_BEFORE_FINALIZATION', label: 'Paid' }, { value: 'OTHER', label: 'Other' }] }],
            })} />
          </div>
        </div>
      )}

      {/* Dialog */}
      {dialog && <ReasonDialog open={!!dialog} onClose={closeDialog} title={dialog.title} onSubmit={dialog.onSubmit} requireReason={dialog.requireReason !== false} fields={dialog.fields || []} />}
    </div>
  );
}
